import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Save, Sparkles, ChevronDown, ChevronUp, Loader2, Eye, EyeOff,
  BookOpen, Calculator, PenLine, MessageSquare, Heart, Activity,
  Users as UsersIcon, FileText, AlertCircle, CheckCircle2
} from 'lucide-react';

import { API_URL } from '../../utils/api';

const SUBSECTION_DEFS = [
  { key: 'current_services', title: 'Current Services Summary', type: 'narrative', ai: true, group: 'Core', icon: FileText,
    hint: 'Summarize the special education and related services the student currently receives.' },
  { key: 'educational_implications', title: 'Educational Implications of Disability', type: 'narrative', ai: true, group: 'Core', icon: AlertCircle,
    hint: 'Describe how the disability impacts access to the general education curriculum.' },
  { key: 'recent_eval_summary', title: 'Recent Evaluation Summary', type: 'narrative', optional: true, group: 'Core', icon: FileText,
    hint: 'Optional summary of the most recent evaluation. Most relevant for initial and reevaluation IEPs.' },
  { key: 'academic_reading', title: 'Reading', type: 'achievement', group: 'Academic Achievement', icon: BookOpen,
    hint: 'Describe current reading performance including current levels, strengths, areas of need, and impact on progress in the general curriculum.' },
  { key: 'academic_writing', title: 'Writing', type: 'achievement', group: 'Academic Achievement', icon: PenLine,
    hint: 'Describe current writing performance including current levels, strengths, areas of need, and impact on progress in the general curriculum.' },
  { key: 'academic_math', title: 'Math', type: 'achievement', group: 'Academic Achievement', icon: Calculator,
    hint: 'Describe current math performance including current levels, strengths, areas of need, and impact on progress in the general curriculum.' },
  { key: 'functional_social_emotional', title: 'Social / Emotional', type: 'achievement', group: 'Functional Performance', icon: Heart,
    hint: 'Describe social-emotional functioning including current levels, strengths, areas of need, and impact on participation and involvement.' },
  { key: 'functional_physical', title: 'Physical', type: 'achievement', group: 'Functional Performance', icon: Activity,
    hint: 'Describe gross/fine motor skills and physical health including current levels, strengths, areas of need, and impact on participation.' },
  { key: 'functional_communication', title: 'Communication', type: 'achievement', group: 'Functional Performance', icon: MessageSquare,
    hint: 'Describe expressive/receptive language and communication including current levels, strengths, areas of need, and impact on participation.' },
  { key: 'parent_input', title: 'Parent / Student Input', type: 'narrative', group: 'Input & Summary', icon: UsersIcon,
    hint: 'Parent concerns, student self-advocacy, and family input.' },
  { key: 'educational_needs_summary', title: 'Summary of Educational Needs', type: 'narrative', group: 'Input & Summary', icon: FileText,
    hint: 'Synthesized summary of all educational needs identified across areas.' },
];

const DEFAULTS = {};
SUBSECTION_DEFS.forEach(s => {
  if (s.type === 'narrative') DEFAULTS[s.key] = { narrative: '', ...(s.optional ? { enabled: true } : {}) };
  else DEFAULTS[s.key] = { narrative: '' };
});

function mergeContent(existing) {
  const merged = { ...DEFAULTS };
  if (!existing || typeof existing !== 'object') return merged;
  // Migrate old {notes: "..."} format
  if (existing.notes && !existing.current_services) {
    merged.educational_needs_summary = { narrative: existing.notes };
    return merged;
  }
  for (const key of Object.keys(DEFAULTS)) {
    if (existing[key]) merged[key] = { ...DEFAULTS[key], ...existing[key] };
  }
  return merged;
}

export default function PLAAFPEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  const [collapsed, setCollapsed] = useState({});
  const [drafting, setDrafting] = useState(null);
  const [draftResult, setDraftResult] = useState({});

  useEffect(() => {
    setContent(mergeContent(section?.content));
  }, [section?.section_id]);

  const updateSub = (key, field, value) => {
    setContent(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const toggleCollapse = (key) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleOptional = async (key, enabled) => {
    updateSub(key, 'enabled', enabled);
    try {
      await axios.patch(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/sections/present_levels/toggle`,
        { subsection: key, enabled },
        { withCredentials: true }
      );
    } catch (err) { console.error('Toggle failed', err); }
  };

  const handleDraft = async (subsectionKey) => {
    setDrafting(subsectionKey);
    try {
      const res = await axios.post(
        `${API_URL}/api/iep-met/cases/${caseData.case_id}/sections/${subsectionKey}/draft`,
        {},
        { withCredentials: true }
      );
      setDraftResult(prev => ({ ...prev, [subsectionKey]: res.data }));
    } catch (err) {
      console.error('Draft failed', err);
    } finally {
      setDrafting(null);
    }
  };

  const acceptDraft = (subsectionKey) => {
    const draft = draftResult[subsectionKey];
    if (!draft?.output?.draft_content) return;
    updateSub(subsectionKey, 'narrative', draft.output.draft_content);
    setDraftResult(prev => { const n = { ...prev }; delete n[subsectionKey]; return n; });
  };

  const handleSave = () => onSave(content);

  const contextType = caseData?.context_type || 'annual';
  const groups = [...new Set(SUBSECTION_DEFS.map(s => s.group))];

  return (
    <div className="max-w-4xl space-y-8" data-testid="plaafp-editor">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="plaafp-title">
            Present Levels / PLAAFP
          </h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#3B6A9B]/10 text-[#3B6A9B] text-xs">{contextType} IEP</Badge>
            {section?.ai_capable && <Badge className="bg-amber-50 text-amber-700 text-xs"><Sparkles className="h-3 w-3 mr-1" />AI-Assisted Subsections</Badge>}
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl shrink-0" data-testid="plaafp-save-all">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* Subsection groups */}
      {groups.map(group => {
        const subs = SUBSECTION_DEFS.filter(s => s.group === group);
        return (
          <div key={group}>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3 px-1">{group}</h3>
            <div className="space-y-4">
              {subs.map(sub => {
                const data = content[sub.key] || {};
                const isOptional = sub.optional;
                const isEnabled = isOptional ? data.enabled !== false : true;
                const isCollapsed = collapsed[sub.key];
                const Icon = sub.icon;
                const hasDraft = draftResult[sub.key];
                const isDrafting = drafting === sub.key;

                return (
                  <div
                    key={sub.key}
                    className={`bg-white border rounded-2xl transition-all ${
                      !isEnabled ? 'border-dashed border-[#E5E5E5] opacity-60' : 'border-[#E5E5E5]'
                    }`}
                    data-testid={`plaafp-card-${sub.key}`}
                  >
                    {/* Card header */}
                    <div className="flex items-center justify-between px-5 py-3.5 cursor-pointer" onClick={() => isEnabled && toggleCollapse(sub.key)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon className="h-4 w-4 text-[#3B6A9B] shrink-0" />
                        <span className="font-medium text-sm text-[#1C1C1C] truncate">{sub.title}</span>
                        {sub.ai && <Sparkles className="h-3 w-3 text-amber-500 shrink-0" />}
                        {isOptional && (
                          <Badge className={`text-[10px] ${isEnabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isEnabled ? 'Included' : 'Excluded'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isOptional && (
                          <button
                            onClick={e => { e.stopPropagation(); toggleOptional(sub.key, !isEnabled); }}
                            className="p-1 rounded-lg hover:bg-gray-100 transition"
                            title={isEnabled ? 'Exclude section' : 'Include section'}
                            data-testid={`toggle-${sub.key}`}
                          >
                            {isEnabled ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                          </button>
                        )}
                        {isEnabled && (isCollapsed ? <ChevronDown className="h-4 w-4 text-[#595959]" /> : <ChevronUp className="h-4 w-4 text-[#595959]" />)}
                      </div>
                    </div>

                    {/* Card body */}
                    {isEnabled && !isCollapsed && (
                      <div className="px-5 pb-5 border-t border-[#F3F4F1]">
                        <p className="text-xs text-[#595959] mt-3 mb-3">{sub.hint}</p>

                        {/* AI Draft result banner */}
                        {hasDraft && hasDraft.status === 'completed' && (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid={`draft-result-${sub.key}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-amber-800">AI Draft Available</span>
                              <div className="flex gap-2">
                                <Button size="sm" className="rounded-lg text-xs bg-green-600 text-white h-7" onClick={() => acceptDraft(sub.key)} data-testid={`accept-draft-${sub.key}`}>
                                  <CheckCircle2 className="h-3 w-3 mr-1" />Accept
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-lg text-xs h-7" onClick={() => setDraftResult(prev => { const n = { ...prev }; delete n[sub.key]; return n; })}>
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-[#595959] whitespace-pre-wrap max-h-32 overflow-y-auto">{hasDraft.output?.draft_content}</p>
                            {hasDraft.output?.notes && <p className="text-xs text-amber-700 mt-2 italic">{hasDraft.output.notes}</p>}
                          </div>
                        )}

                        {sub.type === 'achievement' ? (
                          <div>
                            <div className="bg-[#F3F4F1]/50 border border-[#E5E5E5]/50 rounded-lg p-2.5 mb-3">
                              <p className="text-[10px] text-[#595959] leading-relaxed">Address: current performance level, strengths, areas of need, and impact on progress/involvement in the general curriculum.</p>
                            </div>
                            <Textarea
                              value={data.narrative || ''}
                              onChange={e => updateSub(sub.key, 'narrative', e.target.value)}
                              placeholder={`Describe ${sub.title.toLowerCase()} performance, strengths, needs, and educational impact...`}
                              rows={6}
                              className="rounded-xl border-[#E5E5E5] text-sm"
                              data-testid={`${sub.key}-narrative`}
                            />
                          </div>
                        ) : (
                          <div>
                            <Textarea
                              value={data.narrative || ''}
                              onChange={e => updateSub(sub.key, 'narrative', e.target.value)}
                              placeholder={sub.hint}
                              rows={5}
                              className="rounded-xl border-[#E5E5E5] text-sm"
                              data-testid={`${sub.key}-narrative`}
                            />
                          </div>
                        )}

                        {/* AI Draft button */}
                        {sub.ai && (
                          <div className="mt-3 flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-lg text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                              onClick={() => handleDraft(sub.key)}
                              disabled={isDrafting}
                              data-testid={`draft-btn-${sub.key}`}
                            >
                              {isDrafting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                              {isDrafting ? 'Drafting...' : 'AI Draft'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Bottom save */}
      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#3B6A9B] hover:bg-[#2D5480] text-white rounded-xl" data-testid="plaafp-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save All PLAAFP Content'}
        </Button>
      </div>
    </div>
  );
}
